import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from "@auth0/angular-jwt";
import { Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { IQueryService } from '../interfaces/query.service';
import { Client } from "../models/clients/client.model";
import { DropDownItem } from "../models/fields/dropdown-item.model";
import { TaxonomyField } from "../models/fields/taxonomy-field.model";
import { Taxonomy } from "../models/taxonomies/taxonomy.model";
import { AuthResult } from "../models/users/auth-result.model";
import { environment } from '../../environments/environment';
import { FieldType } from '../enums/field-type.enum';
import { AppHelper } from '../helpers/app.helper';
import { TaxonomyValue } from '../models/excel/taxonomies/taxonomy-value.model';
import { ChildDropDownItem } from '../models/fields/child-dropdown-item.model';
import { SubmitResult } from '../models/taxonomies/submit-result.model';
import { LoggerService } from './logger.service';


@Injectable()
export class QueryService implements IQueryService {
  //#region Fields

  private static readonly defaultDateFormat = 'mm-dd-yyyy';

  private static readonly defaultHeaders: HttpHeaders = new HttpHeaders({
    'content-type': 'application/json'
  });

  private static readonly urlEncodedHeaders: HttpHeaders = new HttpHeaders({
    'content-type': 'application/x-www-form-urlencoded'
  });

  //#endregion

  //#region  C-tor

  constructor(
    private http: HttpClient,
    private logger: LoggerService,
    private appHelper: AppHelper) { }

  //#endregion

  //#region Methods

  login(authCode: string): Observable<AuthResult> {
    return this.post<any>(
      environment.apiRootUrl + '/api/v1/auth',
      `auth_code=${authCode}&redirectURL=${environment.redirectUrl}`,
      `Authorizing the user.. Auth code: '${authCode}'.`,
      QueryService.urlEncodedHeaders)
      .pipe(
        tap(this.updateAuthResult));
  }

  refreshAccessToken(refreshToken: string): Observable<AuthResult> {
    return this.post<AuthResult>(
      environment.apiRootUrl + '/api/v1/auth/refresh',
      '"' + refreshToken + '"',
      'Refreshing the user access token..',
      QueryService.defaultHeaders)
      .pipe(
        tap(this.updateAuthResult));
  }

  getClients(): Observable<Client[]> {
    return this.get<Client[]>(
      environment.apiRootUrl + '/api/v1/clients',
      'Getting a clients list..')
      .pipe(
        tap(v => v.forEach(c => c.name = c.name ? c.name.trim() : '')));
  }

  getTaxonomies(clientId: number): Observable<Taxonomy[]> {
    return this.get<Taxonomy[]>(
      environment.apiRootUrl + '/api/v1/taxonomies/' + clientId,
      `Getting a taxonomies list.. Client id: '${clientId}'.`)
      .pipe(
        map(v => v.filter(t => !t.hide && t.taxonomy_submit)),
        tap(v => v.forEach(t => t.name = t.name ? t.name.trim() : '')));
  }

  getTaxonomyFields(taxonomyId: number): Observable<TaxonomyField[]> {
    return this.get<any>(
      environment.apiRootUrl + '/api/v1/taxonomy/' + taxonomyId,
      `Getting a taxonomy fields list.. Taxonomy id: '${taxonomyId}'.`)
      .pipe(
        map(v => v.fields),
        map(v => v.filter(f => f.submitted)),
        tap(v => v.forEach(f => {
          f.name = f.name ? f.name.trim() : '';
          f.field_desc = f.field_desc ? f.field_desc.trim() : '';
          f.mdm_entity = f.mdm_entity ? f.mdm_entity.trim() : '';
          f.static_field_value = f.static_field_value ? f.static_field_value.trim() : '';
          const displayCode = (f as any).displaycode ?? (f as any).display_code ?? f.displayCode;
          f.displayCode = displayCode ? displayCode.trim() : '';
          f.dateFormat = f.dateFormat ? f.dateFormat.trim() : QueryService.defaultDateFormat;

          if (f.field_type === FieldType.Dropdown && f.fielditems?.length > 0) {
            const dropDownItems: DropDownItem[] = [];

            for (const fi of f.fielditems) {
              let childItems = [];

              if (fi.childitems?.length > 0) {
                childItems = fi.childitems
                  .filter(ci => {
                    if (!ci.Id || !ci.FieldId) {
                      return false;
                    }

                    const id = +ci.Id;
                    const fId = +ci.FieldId;

                    return id !== 0 && fId !== 0 && fi.id !== fId;
                  })
                  .map(c => new ChildDropDownItem(+c.FieldId, c.Id))
                  .filter((ci, index, arr) => this.appHelper.findIndex<any>(arr, i => i.id === ci.id && i.fieldId === ci.fieldId) === index);
              }

              const name = fi.name ? fi.name.trim() : '';
              const code = fi.code ? fi.code.trim() : '';

              dropDownItems.push(new DropDownItem(fi.id, fi.taxonomy_value_id, name, code, childItems));
            }

            f.dropdown_items = dropDownItems;
          }
        })));
  }

  getMdmDropDownItems(taxonomyId: number, fieldId: number): Observable<DropDownItem[]> {
    return this.get<any>(
      environment.apiRootUrl + '/api/v1/taxonomy-fields/' + fieldId + '/cascadingmdmitems/' + taxonomyId,
      `Getting a MDM drop-down items list.. Field id: '${fieldId}'. Taxonomy id: '${taxonomyId}'.`)
      .pipe(
        map(v => v
          .map(i => {
            let childItems = [];

            if (i.childitems?.length > 0) {
              childItems = i.childitems
                .filter(c => {
                  if (!c.Id || !c.FieldId) {
                    return false;
                  }

                  const fId = +c.FieldId;

                  return c.Id && fId !== 0 && fieldId !== fId;
                })
                .map(c => new ChildDropDownItem(+c.FieldId, c.Id))
                .filter((c, index, arr) => this.appHelper.findIndex<any>(arr, i => i.id === c.id && i.fieldId === c.fieldId) === index);
            }

            const name = i.name ? i.name.trim() : '';
            const code = i.code ? i.code.trim() : '';

            return new DropDownItem(i.mdmcode,i.taxonomy_value_id, name, code, childItems);
          })));
  }

  getMdmSearchDropDownItems(taxonomyId: number, filter: string, entityName: string, fieldId: number): Observable<DropDownItem[]> {
    return this.post<any>(
      environment.apiRootUrl + '/api/v1/taxonomy-fields/' + taxonomyId + '/matchingvalues',
      `freetext=${filter}&entityname=${entityName}&fieldid=${fieldId}`,
      `Getting a MDM SEARCH drop-down items list.. Taxonomy id: '${taxonomyId}'. Field id: '${fieldId}|${entityName}'. Filter: '${filter}'.`,
      QueryService.urlEncodedHeaders)
      .pipe(
        map(items => items
          .map(item => {
            const name = item ? item.trim() : '';

            return new DropDownItem(undefined, undefined, name, name);
          })));
  }

  getTaxonomyValues(clientId: number, taxonomyId: number): Observable<DropDownItem[]> {
    return this.get<any>(
      environment.apiRootUrl + '/api/v1/taxonomy-value/' + clientId,
      `Getting a taxonomy values list.. Client id: '${clientId}'. Taxonomy id: '${taxonomyId}'.`)
      .pipe(
        map(v => v
          .filter(v => v['taxonomy_id'] == taxonomyId)
          .map(v => {
            const name = v['display_name'] ? v['display_name'].trim() : '';
            const code = v.name ? v.name.trim() : '';

            return new DropDownItem(undefined, v.taxonomy_value_id, name, code);
          })));
  }

  submitTaxonomyValue(clientId: number, taxonomyId: number, taxonomyValue: TaxonomyValue): Observable<SubmitResult> {
    return this.post<any>(
      environment.apiRootUrl + '/api/v1/client/' + clientId + '/taxonomy-value/' + taxonomyId + '/value',
      {
        value: taxonomyValue.value,
        displayname: taxonomyValue.displayValue,
        targettaxonomyvalue: {}
      },
      `Submitting a taxonomy value.. Taxonomy id: '${taxonomyId}'.`,
      QueryService.defaultHeaders)
      .pipe(
        map<any, SubmitResult>((value, index) => new SubmitResult(value.TaxonomyValueId?.toString(), value.ErrorMessage?.toString())));
  }

  //#endregion

  //#region Helpers

  private appendParams(rootUrl: string, ...objs: any[]): string {
    const queryString = objs
      .map(o => this.toQueryString('', o))
      .filter(s => s)
      .join('&');

    return queryString
      ? rootUrl + '?' + queryString
      : rootUrl;
  }

  private toQueryString(prefix: string, obj: any): string {
    const arr: string[] = [];

    for (const prop in obj) {
      if (obj.hasOwnProperty(prop)) {
        const key = prefix ? prefix + '.' + prop : prop;
        const value = obj[prop];

        if (value !== undefined) {
          if (Array.isArray(value)) {
            for (const item of value) {
              arr.push(encodeURIComponent(key) + '=' + encodeURIComponent(item));
            }
          } else {
            const queryParam = value !== null && typeof value === 'object'
              ? this.toQueryString(key, value)
              : encodeURIComponent(key) + '=' + encodeURIComponent(value);

            if (queryParam) {
              arr.push(queryParam);
            }
          }
        }
      }
    }

    return arr.join('&');
  }

  private get<T>(url: string, operation: string): Observable<T> {
    this.logger.info(operation);
    return this.http.get<T>(url);
  }

  private post<T>(url: string, data: any, operation: string, headers?: HttpHeaders): Observable<T> {
    this.logger.info(operation);
    return this.http.post<T>(url, data, { headers: headers });
  }

  private updateAuthResult(authResult: AuthResult): void {
    const issued = new Date();
    const expires = new Date();

    expires.setSeconds(expires.getSeconds() + authResult.expires_in);

    authResult.issued_date = issued;
    authResult.expiration_date = expires;

    if (!environment.fakeData) {
      authResult.userId = new JwtHelperService().decodeToken(authResult.access_token)['username'];
    }
  }

  //#endregion
}
